import { openSpeechStream, type SpeechStream } from '@hugo/ai/speech-client'
import type { SpeechSession } from '@hugo/core'
import { useCallback, useEffect, useRef, useState } from 'react'
import { log } from '../log'
import { callTool } from '../tools'
import { type CaptureError, CaptureFailed, startCapture } from './capture'

/** Why dictation stopped without text. */
export type DictationError =
  | CaptureError
  /** This server takes no dictation, or hugo could not open a session. */
  | 'unavailable'
  /** The speech service dropped or never answered. */
  | 'connection'
  /** The service heard nothing it could write down. */
  | 'nothing-heard'

export type Dictation =
  | { phase: 'idle' }
  /** Asking hugo for a session and the browser for the microphone. */
  | { phase: 'starting' }
  /** Streaming audio; `partial` is what was understood so far, `level` the input level (0..1). */
  | { phase: 'listening'; partial: string; level: number }
  /** Stopped; waiting for the final transcript. */
  | { phase: 'finishing'; partial: string }
  | { phase: 'error'; error: DictationError; partial: string }

/** How long to wait for the final transcript after stopping. */
const FINAL_TIMEOUT_MS = 5_000

type Run = {
  id: number
  stream: SpeechStream | null
  capture: { stop: () => void } | null
  partial: string
  finalTimer: ReturnType<typeof setTimeout> | null
  /** Audio captured before the stream was open (sent once it is). */
  pending: ArrayBuffer[]
  /** The user stopped before the stream was open. */
  stopRequested: boolean
}

/** Audio held back until the stream is open: at most 5 s. */
const PENDING_MAX = 50

/**
 * Dictation: the microphone starts, hugo hands out a short-lived speech session, the audio
 * streams at 16 kHz straight to the speech service, and the transcript comes back here. Only the
 * text is passed on (`onText`); no audio reaches hugo or is kept.
 */
export function useDictation(onText: (text: string) => void) {
  const [state, setState] = useState<Dictation>({ phase: 'idle' })
  const run = useRef<Run | null>(null)
  const seq = useRef(0)
  const textRef = useRef(onText)
  textRef.current = onText

  /** Stop the microphone and the stream of a run, once. */
  const release = useCallback((current: Run, cancelStream: boolean) => {
    current.capture?.stop()
    current.capture = null
    if (current.finalTimer) clearTimeout(current.finalTimer)
    if (cancelStream) current.stream?.cancel()
    if (run.current?.id === current.id) run.current = null
  }, [])

  const fail = useCallback(
    (current: Run, error: DictationError) => {
      release(current, true)
      setState({ phase: 'error', error, partial: current.partial })
    },
    [release],
  )

  const start = useCallback(async () => {
    if (run.current) return
    seq.current += 1
    const current: Run = {
      id: seq.current,
      stream: null,
      capture: null,
      partial: '',
      finalTimer: null,
      pending: [],
      stopRequested: false,
    }
    run.current = current
    setState({ phase: 'starting' })
    const live = () => run.current?.id === current.id

    // The session from hugo and the microphone in parallel; the speech service is only contacted
    // once the microphone runs (a blocked microphone opens no session).
    const session = callTool('voice.session', {}).then(({ result }) => result.session)
    session.catch(() => {})
    try {
      const capture = await startCapture((frame) => {
        if (!live()) return
        if (current.stream) current.stream.sendAudio(frame.pcm)
        else if (current.pending.length < PENDING_MAX) current.pending.push(frame.pcm)
        setState((s) => (s.phase === 'listening' ? { ...s, level: frame.rms } : s))
      })
      if (!live()) {
        capture.stop()
        return
      }
      current.capture = capture
      setState({ phase: 'listening', partial: '', level: 0 })
    } catch (error) {
      if (live()) fail(current, error instanceof CaptureFailed ? error.reason : 'failed')
      return
    }

    let opened: SpeechSession
    try {
      opened = await session
    } catch (error) {
      log.warn('voice session failed', String(error))
      if (live()) fail(current, 'unavailable')
      return
    }
    if (!live()) return
    current.stream = openSpeechStream(opened, {
      onPartial: (text) => {
        if (!live()) return
        current.partial = text
        setState((s) =>
          s.phase === 'listening'
            ? { ...s, partial: text }
            : s.phase === 'finishing'
              ? { phase: 'finishing', partial: text }
              : s,
        )
      },
      onFinal: (text) => {
        if (!live()) return
        release(current, false)
        const heard = text.trim()
        if (heard) {
          setState({ phase: 'idle' })
          textRef.current(heard)
        } else {
          setState({ phase: 'error', error: 'nothing-heard', partial: '' })
        }
      },
      onError: (error, message) => {
        if (!live()) return
        log.warn('speech stream failed', `${error}: ${message}`)
        // Keep what was understood: the user may still use it.
        if (current.partial) textRef.current(current.partial)
        fail(current, 'connection')
      },
    })
    for (const pcm of current.pending.splice(0)) current.stream.sendAudio(pcm)
    if (current.stopRequested) current.stream.stop()
  }, [fail, release])

  /** Done speaking: the service sends the final transcript. */
  const stop = useCallback(() => {
    const current = run.current
    if (!current?.capture) return
    current.capture.stop()
    current.capture = null
    // Before the stream is open, it stops right after sending what was captured.
    if (current.stream) current.stream.stop()
    else current.stopRequested = true
    current.finalTimer = setTimeout(() => fail(current, 'connection'), FINAL_TIMEOUT_MS)
    setState({ phase: 'finishing', partial: current.partial })
  }, [fail])

  /** Give up: no text. */
  const cancel = useCallback(() => {
    const current = run.current
    if (current) release(current, true)
    setState({ phase: 'idle' })
  }, [release])

  /** Forget the last error. */
  const reset = useCallback(() => {
    setState((s) => (s.phase === 'error' ? { phase: 'idle' } : s))
  }, [])

  // Leaving the page or closing the dialog never leaves the microphone on.
  useEffect(() => () => run.current?.capture?.stop(), [])

  const active =
    state.phase === 'starting' || state.phase === 'listening' || state.phase === 'finishing'
  return { state, active, start, stop, cancel, reset }
}

/** What to tell the user when dictation stopped without text. */
export function dictationErrorText(error: DictationError, kept: boolean): string {
  switch (error) {
    case 'denied':
      return "Microphone access is blocked. Allow it in your browser's site settings, or type instead."
    case 'no-microphone':
      return 'No microphone found. Type instead.'
    case 'unsupported':
      return 'This browser cannot record audio. Type instead.'
    case 'failed':
      return 'The microphone could not start. Type instead.'
    case 'unavailable':
      return 'Voice is not available right now. Type instead.'
    case 'connection':
      return kept
        ? 'Lost the connection to the speech service. What was heard is in the field.'
        : 'Lost the connection to the speech service. Try again, or type it.'
    case 'nothing-heard':
      return "Didn't catch that. Try again, or type it."
  }
}
